
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

interface TourContextProps {
  showTour: boolean;
  startTour: () => void;
  endTour: () => void;
}

const TourContext = createContext<TourContextProps>({
  showTour: false,
  startTour: () => {},
  endTour: () => {},
});

export const useTour = () => useContext(TourContext);

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showTour, setShowTour] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  // Load saved tour state when component mounts
  useEffect(() => {
    const loadTourState = async () => {
      if (isNative) {
        try {
          const { value } = await Preferences.get({ key: 'tourCompleted' });
          // If tourCompleted is not set, we consider the tour not completed
          const hasCompletedTour = value === 'true';
          
          // For debugging - log the stored preference
          console.log('Stored tour preference:', value);
          
          // On first app load for new users, we might want to show the tour
          // automatically if they haven't completed it before
          if (value === null) {
            // This is potentially a new user, might want to auto-start tour
            console.log('No stored tour preference found - new user');
          }
        } catch (error) {
          console.error('Error loading tour state:', error);
        }
      }
    };

    loadTourState();
  }, [isNative]);

  const startTour = () => {
    console.log('Starting tour...');
    setShowTour(true);
  };

  const endTour = async () => {
    console.log('Ending tour...');
    setShowTour(false);
    
    // Save tour state in native apps
    if (isNative) {
      try {
        await Preferences.set({
          key: 'tourCompleted',
          value: 'true'
        });
        console.log('Tour completion state saved');
      } catch (error) {
        console.error('Error saving tour state:', error);
      }
    }
  };

  return (
    <TourContext.Provider value={{ showTour, startTour, endTour }}>
      {children}
    </TourContext.Provider>
  );
};
